import React from 'react';
import { useSelector } from 'react-redux';
import { Button, OnboardingFooter, OnboardingHeader, Text } from 'components';
import Illustration from 'images/illustrations/MoneyTransfer-opaque.svg';
import { ErrorScreen } from 'screens';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Invites, selectInvite } from 'store/Invites';
import { selectSignUpUser } from 'store/SignUp';
import { IStore } from 'store/types';
import { selectUserInvites } from 'store/User/selectors';
import { EmployeeActionRequiredType, EmployeeRole, EmployeeStatus, EmployeeType, IEmployeeSite, IInvite, InvitationDisplay, SiteRole } from 'types';
import { getInvitedSitesList } from 'utils';
import { isOrgAdmin, orgRoleDisplay, siteRoleToEmployeeRole } from 'utils/employee';
import { messagingRequiresAction, messagingSuccess } from './Messaging';

interface ActionRequiredProps {
  status: EmployeeStatus;
  pushRouteOnEnd: () => void;
}

export const InvitationAcceptedResult = ({ status, pushRouteOnEnd }: ActionRequiredProps) => {
  const invite = useSelector(selectInvite);
  const userInvites = useSelector(selectUserInvites);
  const signUpUser = useSelector(selectSignUpUser());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Invites.GET_INVITE_REQUEST));

  if (!invite && !userInvites && !isLoading) return <ErrorScreen />;

  // Invite List
  // TODO Add logic to handle multisite accepted result page
  const invitedSitesList: InvitationDisplay[] = (() => {
    if (userInvites) return getInvitedSitesList(userInvites);

    const orgName = invite?.employee?.organization.name;
    const isAdmin = invite?.employee?.type === EmployeeType.ADMIN;

    // Return organizaton admin
    if (isAdmin) {
      return [
        {
          orgName: orgName || '',
          role: SiteRole.ADMIN,
          displayRole: (invite && orgRoleDisplay(invite.employee.type)) || '',
          site: orgName,
        },
      ];
    }

    return (
      invite?.employee?.sites?.map((site: IEmployeeSite) => ({
        orgName: orgName || '',
        role: site.role,
        displayRole: orgRoleDisplay(invite.employee.type, site.role) || '',
        siteName: isAdmin ? orgName : site.name,
      })) || []
    );
  })();

  // Handle multi-invite or multi-org acceptance
  if (userInvites && !status) {
    const inviteStatusArr: string[] = userInvites.reduce((arr: string[], invite) => {
      if (invite.employee.actionRequired) arr.push(invite.employee.actionRequired);
      if (invite.status) arr.push(invite.status);
      return arr;
    }, []);

    const isMultiInvite = userInvites.length > 1 || invitedSitesList.length > 1;
    const acceptedText = isMultiInvite ? 'Invitations Accepted' : 'Invitation Accepted';
    const getInviteAcceptMessage = () => {
      if (inviteStatusArr.includes(EmployeeActionRequiredType.EMPLOYER_APPROVAL || EmployeeStatus.ACTION_REQUIRED)) {
        return <span data-cy="messaging-accepted-pending">Some sites are pending employer approval. Once approved you will be able to access your new roles.</span>;
      }

      return <span data-cy="messaging-accepted">{`Thank you for accepting your ${isMultiInvite ? 'invitations' : 'invitation'}. You can now access your new roles. Be sure to enter your debit card if you wish to receive payments.`}</span>;
    };

    return (
      <>
        <OnboardingHeader data-cy="user-invites-accepted" title={inviteStatusArr.includes(EmployeeStatus.ACTION_REQUIRED) ? 'Sent To Manager For Approval' : acceptedText} />
        <img alt="Money Transfer Illustration" aria-hidden="true" src={Illustration} />
        <Text size="default">{getInviteAcceptMessage()}</Text>
        <OnboardingFooter>
          <Button data-cy="action-btn" label={!signUpUser ? 'Use Kickfin' : 'Complete account set up'} onClick={pushRouteOnEnd} />
        </OnboardingFooter>
      </>
    );
  }

  // Handle single-invite acceptance
  const acceptedInvitation: IInvite = userInvites ? userInvites[0] : invite;
  const isOrganizationAdmin = acceptedInvitation && isOrgAdmin(acceptedInvitation.employee);
  const isMultiInvite = !isOrganizationAdmin && invitedSitesList && invitedSitesList?.length > 1;
  const acceptedText = isMultiInvite ? 'Invitations Accepted' : 'Invitation Accepted';
  const messageEntity: string | undefined = acceptedInvitation?.employee && isOrganizationAdmin ? invitedSitesList[0]?.orgName : invitedSitesList[0]?.siteName;

  // TODO: set inviteRole to the highest privileged invite role

  const getInviteRole = () => {
    const displayAdmin = acceptedInvitation && isOrganizationAdmin;
    const firstSite = acceptedInvitation?.employee?.sites?.[0];
    const siteRole = firstSite && siteRoleToEmployeeRole[firstSite.role];
    return displayAdmin ? EmployeeRole.ADMIN : siteRole;
  };

  return (
    <>
      <OnboardingHeader data-cy="user-invites-accepted" title={status === EmployeeStatus.ACTION_REQUIRED ? 'Sent To Manager For Approval' : acceptedText} />

      <img alt="Money Transfer Illustration" aria-hidden="true" src={Illustration} />

      <Text size="default">{status === EmployeeStatus.ACTION_REQUIRED ? messagingRequiresAction(getInviteRole(), messageEntity as string, isMultiInvite) : messagingSuccess(getInviteRole(), messageEntity as string, isMultiInvite)}</Text>

      <OnboardingFooter>
        <Button data-cy="action-btn" label={!signUpUser ? 'Use Kickfin' : 'Complete account set up'} onClick={pushRouteOnEnd} />
      </OnboardingFooter>
    </>
  );
};
